// TableDumpDialog — single-table version of BackupDialog. Opened from a
// table's right-click "Dump Table into SQL…" menu item. Streams the dump
// straight to a browser download via the two-step POST /api/table-dump →
// GET /api/table-dump/{token} flow.
//
// Props:
//   connectionId  number
//   database      string
//   table         string
//   onClose       fn()
import { createSignal, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { downloadTableDump } from '../lib/export.js';
import { toast } from '../lib/toast.js';

export default function TableDumpDialog(props) {
  // mode: "data+structure" (default — CREATE TABLE + INSERTs) | "data-only" (just INSERTs)
  // The "structure-only" mode is supported by the API but not exposed here —
  // "Copy Schema (SQL)" already covers the no-data case (clipboard, no download).
  const [mode, setMode]           = createSignal('data+structure');
  const [addDrop, setAddDrop]     = createSignal(false);
  const [triggers, setTriggers]   = createSignal(true);
  const [gzip, setGzip]           = createSignal(false);
  const [singleTxn, setSingleTxn] = createSignal(true);
  const [stripDef, setStripDef]   = createSignal(false);
  const [busy, setBusy]           = createSignal(false);

  const includesStructure = () => mode() === 'data+structure';

  const start = async () => {
    setBusy(true);
    try {
      await downloadTableDump({
        connectionId: props.connectionId,
        database:     props.database,
        table:        props.table,
        mode:         mode(),
        addDropTable: addDrop(),
        includeTriggers: triggers(),
        gzip:         gzip(),
        singleTransaction: singleTxn(),
        stripDefiner: stripDef(),
      });
      toast.success('Dump started — check your downloads.');
      props.onClose?.();
    } catch (e) {
      toast.error('Dump failed: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const Check = (cprops) => (
    <label class="backup-opt">
      <input
        type="checkbox"
        checked={cprops.value}
        disabled={cprops.disabled}
        onChange={(e) => cprops.onChange(e.target.checked)}
      />
      <span>
        <span class="backup-opt-label">{cprops.label}</span>
        <Show when={cprops.hint}><span class="backup-opt-hint">{cprops.hint}</span></Show>
      </span>
    </label>
  );

  return (
    <Modal open={true} title={`Dump table — ${props.database}.${props.table}`} onClose={props.onClose}>
      <div class="backup-dialog">
        <p class="muted" style="margin-top:0;">
          Streams a SQL dump of <span class="mono">{props.database}.{props.table}</span> straight
          to a download. Uses this server's own <span class="mono">SHOW CREATE TABLE</span> output,
          so the DDL matches this server's version.
        </p>

        <div class="backup-section-title">Mode</div>
        <label class="backup-opt">
          <input type="radio" name="tdmode"
                 checked={mode() === 'data+structure'}
                 onChange={() => setMode('data+structure')} />
          <span>
            <span class="backup-opt-label">Data + structure</span>
            <span class="backup-opt-hint">CREATE TABLE followed by extended INSERTs — the usual case.</span>
          </span>
        </label>
        <label class="backup-opt">
          <input type="radio" name="tdmode"
                 checked={mode() === 'data-only'}
                 onChange={() => setMode('data-only')} />
          <span>
            <span class="backup-opt-label">Data only</span>
            <span class="backup-opt-hint">Extended INSERTs — restore target must already have the table.</span>
          </span>
        </label>

        <div class="backup-section-title">Options</div>
        <Check
          value={addDrop()}
          disabled={!includesStructure()}
          onChange={setAddDrop}
          label="Add DROP TABLE IF EXISTS"
          hint={includesStructure()
            ? 'Restored as a fresh table — destroys any existing rows under the same name.'
            : 'Only meaningful when "Data + structure" is selected.'}
        />
        <Check
          value={triggers()}
          disabled={!includesStructure()}
          onChange={setTriggers}
          label="Include triggers"
          hint={includesStructure()
            ? "Each trigger declared on this table is emitted after its CREATE TABLE."
            : 'Only meaningful when "Data + structure" is selected.'}
        />
        <Check
          value={singleTxn()}
          onChange={setSingleTxn}
          label="Consistent snapshot"
          hint="Single transaction — point-in-time consistent for InnoDB rows."
        />
        <Check
          value={gzip()}
          onChange={setGzip}
          label="Compress (.sql.gz)"
        />
        <Check
          value={stripDef()}
          disabled={!includesStructure()}
          onChange={setStripDef}
          label="Strip DEFINER clauses"
          hint={includesStructure()
            ? 'Restore triggers under the importing account.'
            : 'Only meaningful when "Data + structure" is selected.'}
        />

        <div class="backup-actions">
          <button class="btn btn-ghost" onClick={props.onClose} disabled={busy()}>Cancel</button>
          <button class="btn btn-primary" onClick={start} disabled={busy()}>
            {busy() ? '⏳ Preparing…' : '⬇ Download dump'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
